import React, { useEffect, useState } from "react";
import { Checkbox, Col, Divider, Input, Row, message } from "antd";
import StepItemWidget from "./StepItemWidget";
import { capitalizeFirstLetter, getInitData, renderTextWithLinks } from "utils/GlobalFunctions";
import CommonButton from "components/ButtonWidgets/CommonButton/CommonButton";
import StepRegItemWidget from "./StepRegItemWidget";
import { showMyAlertDialog } from "components/MyAlertDialog/showMyAlertDialog";
import moment from "moment";

const StepFee = (props) => {
  const { userData, setUserData, errorField, setErrorField, regType, curDomain, appInfo, isLinkedReg } = props;

  // DEBUG: Verify this code is running
  console.log('=== StepFee LOADED ===', {
    registrationType: appInfo?.registration_info?.registration_type,
    regListLength: userData?.regList?.length
  });

  // Check if this is a facility registration type
  const regTypeValue = (appInfo?.registration_info?.registration_type || "")
    .toString()
    .trim()
    .toLowerCase();
  const isFacilityType = regTypeValue === "facility";

  const getHourlyConfig = (regInfo) => {
    const regHoursIncluded = parseInt(regInfo?.hours_included ?? regInfo?.hoursIncluded ?? 0) || 0;
    const regAddlPerHourFee = parseFloat(regInfo?.addl_per_hour_fee ?? regInfo?.addlPerHourFee ?? 0) || 0;
    const fallbackHoursIncluded = parseInt(appInfo?.registration_info?.hours_included ?? appInfo?.registration_info?.hoursIncluded ?? 0) || 0;
    const fallbackAddlPerHourFee = parseFloat(appInfo?.registration_info?.addl_per_hour_fee ?? appInfo?.registration_info?.addlPerHourFee ?? 0) || 0;

    return {
      hoursIncluded: regHoursIncluded > 0 ? regHoursIncluded : fallbackHoursIncluded,
      addlPerHourFee: regAddlPerHourFee > 0 ? regAddlPerHourFee : fallbackAddlPerHourFee,
      raw: {
        regHoursIncluded,
        regAddlPerHourFee,
        fallbackHoursIncluded,
        fallbackAddlPerHourFee
      }
    };
  };

  const calculateTotalHours = (startTime, endTime) => {
    if (!startTime || !endTime) return null;
    const start = moment(startTime, ["hh:mm A", "HH:mm:ss", "HH:mm", "h:mm A"]);
    const end = moment(endTime, ["hh:mm A", "HH:mm:ss", "HH:mm", "h:mm A"]);
    if (!start.isValid() || !end.isValid()) return null;
    let diffHours = end.diff(start, 'hours', true);
    if (diffHours <= 0) diffHours += 24; // handle overnight
    return Math.ceil(diffHours);
  };

  const recalculateHourlyFees = (camperList, camperIndex, totalHours) => {
    const camper = camperList[camperIndex];
    if (camper.sel_reg_id && camper.sel_reg_id.length > 0) {
      let totalRealFee = 0;
      let totalSrcFee = 0;

      camperList[camperIndex].sel_reg_id = camper.sel_reg_id.map(selReg => {
        const regInfo = userData.regList.find(r => r.ID == selReg.id);
        const baseFee = parseFloat(regInfo?.registration_fee || selReg.fee || 0);
        const hourlyConfig = getHourlyConfig(regInfo || selReg);
        const hoursIncluded = hourlyConfig.hoursIncluded;
        const addlPerHourFee = hourlyConfig.addlPerHourFee;

        const additionalHours = Math.max(0, totalHours - hoursIncluded);
        const hourlyCharge = additionalHours * addlPerHourFee;

        const srcFee = baseFee + hourlyCharge;
        const realFee = srcFee - (selReg.zip_discount || 0) - (selReg.sibling_discount || 0);

        totalSrcFee += srcFee;
        totalRealFee += realFee;

        return {
          ...selReg,
          hourly_charge: hourlyCharge,
          real_fee: realFee
        };
      });

      camperList[camperIndex].src_fee = totalSrcFee;
      camperList[camperIndex].real_fee = totalRealFee;
    }
    return camperList;
  };

  useEffect(() => {
    if (!isFacilityType || !userData?.camperInfo) return;

    let changed = false;
    let tmpList = [...userData.camperInfo];

    tmpList.forEach((camper, key) => {
      const startTime = camper.event_start_time || userData.event_start_time || "";
      const endTime = camper.event_end_time || userData.event_end_time || "";
      const calculated = calculateTotalHours(startTime, endTime);

      if (calculated !== null && camper.total_hours !== calculated) {
        tmpList[key] = { ...tmpList[key], total_hours: calculated };
        tmpList = recalculateHourlyFees(tmpList, key, calculated);
        changed = true;
      }
    });

    if (changed) {
      setUserData({
        ...userData,
        camperInfo: [...tmpList],
      });
    }
  }, [
    isFacilityType,
    userData?.camperInfo?.map(c => `${c.event_start_time || ''}-${c.event_end_time || ''}`).join('|'),
    userData?.event_start_time,
    userData?.event_end_time
  ]);

  const onCheck = async (key, id) => {
    let tmpList = [...userData.camperInfo];
    let tmpTripList = [...userData.tripList];
    let tripKey = tmpTripList.findIndex((x) => x.id == id);
    let selTrip = tmpTripList[tripKey]

    for (let i = 0; i < tmpList.length; i++) {
      let itemInfo = tmpList[i];
      if (i == key) {
        if (itemInfo.sel_trip.includes(id)) {
          let tmpCheckedFees = [...itemInfo.sel_trip.split(",")];
          tmpCheckedFees = tmpCheckedFees.filter((x) => x != "" && x != id);
          let newIDs = tmpCheckedFees.join(",");
          if (newIDs != "") {
            newIDs = newIDs + ",";
          }
          itemInfo.sel_trip = newIDs;
          selTrip.current_registrations = parseInt(selTrip.current_registrations) - 1
        } else {
          if (parseInt(selTrip.max_registrations) === parseInt(selTrip.current_registrations)) {
            await showMyAlertDialog({
              appInfo,
              title: "Registration Full",
              content: `No more registrations are available for this program. To be put on a waitlist email ${appInfo?.registration_info?.admin_email} with the program that you wish to be waitlisted on.`
            });
          } else {
            itemInfo.sel_trip = itemInfo.sel_trip + id + ",";
            selTrip.current_registrations = parseInt(selTrip.current_registrations) + 1
          }
        }
      }
      tmpList[i] = itemInfo;
    }
    tmpTripList[tripKey] = selTrip
    setUserData({
      ...userData,
      tripList: [...tmpTripList],
      camperInfo: [...tmpList],
    });
  };

  const onCheckReg = async (key, id) => {
    let tmpList = [...userData.camperInfo];
    let tmpRegList = [...userData.regList];
    let regKey = tmpRegList.findIndex((x) => x.ID == id);
    let selReg = tmpRegList[regKey]

    let selCamp = tmpList[key];


    if (selCamp.sel_reg_id.some(t => t.id === id)) {
      selCamp.sel_reg_id = selCamp.sel_reg_id.filter((x) => x.id != id);
      selReg.current_registrations = parseInt(selReg.current_registrations) - 1
    } else {
      if (parseInt(selReg.current_registrations) >= parseInt(selReg.max_registrations)) {
        await showMyAlertDialog({
          appInfo,
          title: "Registration Full",
          content: `No more registrations are available for this program. To be put on a waitlist email ${appInfo?.registration_info?.admin_email} with the program that you wish to be waitlisted on.`
        });
        return;
      } else {

        ////////////discount-part-start
        if (selCamp.camper_zip) {
          if (selReg.verify_zipcode == "1") {
            if (selReg.zip_code_list.includes(selCamp.camper_zip?.toString()) || selReg.zip_code_list.includes(userData.camper_zip?.toString())) {
              selCamp.zip_discount = parseFloat(selReg.zip_discount_amount)
              selCamp.zip_include = true
            } else {
              selCamp.zip_discount = 0
              selCamp.zip_include = false
              message.error("Camper's Zip Code is not eligible for this Registration")
              return;
            }
          } else {

            if (selReg.zip_code_list.includes(selCamp.camper_zip?.toString()) || selReg.zip_code_list.includes(userData.camper_zip?.toString())) {
              selCamp.zip_discount = parseFloat(selReg.zip_discount_amount)
              selCamp.zip_include = true
            } else {
              selCamp.zip_discount = 0
              selCamp.zip_include = false
            }
          }
        } else {
          selCamp.zip_discount = 0
          selCamp.zip_include = false
        }
        if (key == 0) {
          selCamp.sibling_discount = 0
        } else {
          if (selReg.sibling_discount == "1") {
            selCamp.sibling_discount = parseFloat(selReg.sibling_discount_amount)
          } else {
            selCamp.sibling_discount = 0
          }
        }

        selCamp.sibling_include = selReg.sibling_discount == "1"

        const feeType = selReg.fee_type || "fixed";
        const isVariable = feeType === "variable";

        // Calculate base fee with hourly charges for facility types
        const baseFee = parseFloat(selReg.registration_fee);
        const hourlyConfig = getHourlyConfig(selReg);
        const hoursIncluded = hourlyConfig.hoursIncluded;
        const addlPerHourFee = hourlyConfig.addlPerHourFee;
        const totalHours = parseInt(selCamp.total_hours || 0);

        // Debug: Log hourly fee values from registration
        console.log('Hourly fee debug:', {
          registrationId: id,
          baseFee,
          hoursIncluded,
          addlPerHourFee,
          totalHours,
          isFacilityType,
          feeType,
          rawHoursIncluded: selReg.hours_included,
          rawAddlPerHourFee: selReg.addl_per_hour_fee,
          fallbackHoursIncluded: hourlyConfig.raw.fallbackHoursIncluded,
          fallbackAddlPerHourFee: hourlyConfig.raw.fallbackAddlPerHourFee
        });

        // Calculate hourly charges if total_hours is set
        let hourlyCharge = 0;
        if (!isVariable && isFacilityType && totalHours > 0 && addlPerHourFee > 0) {
          const additionalHours = Math.max(0, totalHours - hoursIncluded);
          hourlyCharge = additionalHours * addlPerHourFee;
        }

        const srcFee = isVariable ? 0 : (baseFee + hourlyCharge);
        selCamp.src_fee += srcFee;
        selCamp.real_fee += isVariable ? 0 : (srcFee - selCamp.zip_discount - selCamp.sibling_discount);

        let tmpSelRegObj = {
          id: id,
          desc: selReg.registration_text,
          fee: selReg.registration_fee,
          fee_type: feeType,
          hours_included: hoursIncluded,
          addl_per_hour_fee: addlPerHourFee,
          hourly_charge: isVariable ? 0 : hourlyCharge,
          zip_discount: isVariable ? 0 : selCamp.zip_discount,
          sibling_discount: isVariable ? 0 : selCamp.sibling_discount,
          real_fee: isVariable ? 0 : (srcFee - selCamp.zip_discount - selCamp.sibling_discount)
        }
        selCamp.sel_reg_id = [...selCamp.sel_reg_id, tmpSelRegObj]
        selReg.current_registrations = parseInt(selReg.current_registrations) + 1
      }
    }


    tmpList[key] = selCamp
    tmpRegList[regKey] = selReg

    setUserData({
      ...userData,
      regList: [...tmpRegList],
      camperInfo: [...tmpList],
    });
  }

  const onVariableFeeChange = (camperIndex, regId, amount) => {
    let tmpList = [...userData.camperInfo];
    const selCamp = { ...tmpList[camperIndex] };

    selCamp.sel_reg_id = selCamp.sel_reg_id.map(reg =>
      reg.id === regId ? { ...reg, real_fee: amount } : reg
    );

    selCamp.real_fee = selCamp.sel_reg_id.reduce((sum, reg) => sum + parseFloat(reg.real_fee || 0), 0);
    selCamp.src_fee = selCamp.sel_reg_id.reduce((sum, reg) =>
      sum + parseFloat(reg.real_fee || 0) + parseFloat(reg.zip_discount || 0) + parseFloat(reg.sibling_discount || 0), 0
    );

    tmpList[camperIndex] = selCamp;
    setUserData({ ...userData, camperInfo: [...tmpList] });
  };

  return (
    <div className="profile-info-widget">
      <div>

        {!isLinkedReg && (
        <div>
          <Row justify="space-between" align="middle">
            <Col>
              <span className="title">Participant Registrations</span>
            </Col>
          </Row>
          <Divider></Divider>
          {(userData.camperInfo || []).map((camper, key) => {
            return (
              <div key={key}>
                <div
                  style={{
                    fontSize: 16,
                    color: "var(--blueColor)",
                    marginTop: 10,
                  }}
                >
                  {camper?.camper_first_name && camper?.camper_last_name ?
                    camper?.camper_first_name + " " + camper?.camper_last_name + " Registration" : userData?.p_first_name && userData?.p_last_name ? userData?.p_first_name + " " + userData?.p_last_name + " Registration" : "Participant " + (key + 1) + " Registration"}
                </div>
                <div className="block" style={{ marginTop: 10, padding: 10 }}>
                  {(userData.regList || []).filter(x => x.open_close !== "0" && x.open_close !== 0).map((x, index, filteredArr) => {
                    return (
                      <StepRegItemWidget
                        itemInfo={x}
                        onCheck={onCheckReg}
                        onVariableFeeChange={onVariableFeeChange}
                        key={index}
                        camperIndex={key}
                        isLast={index == filteredArr.length - 1}
                        userData={userData}
                      />
                    );
                  })}
                  {/* Total Hours input for facility registrations */}
                  {isFacilityType && camper.sel_reg_id && camper.sel_reg_id.length > 0 && (() => {
                    const startTime = camper.event_start_time || userData.event_start_time || "";
                    const endTime = camper.event_end_time || userData.event_end_time || "";
                    const hasAutoHours = calculateTotalHours(startTime, endTime) !== null;
                    return (
                    <div style={{ marginTop: 15, padding: "10px 15px", backgroundColor: "#f5f5f5", borderRadius: 4 }}>
                      <Row gutter={16} align="middle">
                        <Col>
                          <span style={{ fontWeight: 600, color: "var(--blueColor)" }}>
                            Total Hours for Event:
                          </span>
                        </Col>
                        <Col>
                          <Input
                            type="number"
                            min={1}
                            value={camper.total_hours || ""}
                            placeholder={hasAutoHours ? "Auto-calculated" : "Enter hours"}
                            style={{ width: 48, color: "#000" }}
                            className="common-input"
                            readOnly={hasAutoHours}
                            onChange={(e) => {
                              if (hasAutoHours) return;
                              const totalHours = parseInt(e.target.value) || 0;
                              let tmpList = [...userData.camperInfo];
                              tmpList[key].total_hours = totalHours;
                              tmpList = recalculateHourlyFees(tmpList, key, totalHours);
                              setUserData({
                                ...userData,
                                camperInfo: [...tmpList],
                              });
                            }}
                          />
                        </Col>
                        <Col>
                          <span style={{ fontSize: 12, color: "#666" }}>
                            {(() => {
                              const selRegInfo = camper.sel_reg_id?.[0];
                              if (!selRegInfo) return "";
                              const regInfo = userData.regList?.find(r => r.ID == selRegInfo.id);
                              const hoursIncluded = parseInt(regInfo?.hours_included || 0);
                              const addlPerHourFee = parseFloat(regInfo?.addl_per_hour_fee || 0);
                              if (hoursIncluded > 0 || addlPerHourFee > 0) {
                                return `(${hoursIncluded} hrs included, $${addlPerHourFee.toFixed(2)}/hr after)`;
                              }
                              return "";
                            })()}
                          </span>
                        </Col>
                      </Row>
                    </div>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
        )}
        {(userData.tripList || []).length > 0 &&
          <div style={{ marginTop: 20 }}>
            <span className="title">
              Extra Programs
            </span>
            <Divider></Divider>
            {(userData.camperInfo || []).map((camper, key) => {
              return (
                <div key={key}>
                  <div
                    style={{
                      fontSize: 16,
                      color: "var(--blueColor)",
                      marginTop: 10,
                    }}
                  >
                    {camper?.camper_first_name && camper?.camper_last_name ?
                      camper?.camper_first_name + " " + camper?.camper_last_name + " Extra Programs" : userData?.p_first_name && userData?.p_last_name ? userData?.p_first_name + " " + userData?.p_last_name + " Extra Programs" : "Participant " + (key + 1) + " Extra Programs"}
                  </div>
                  <div className="block" style={{ marginTop: 10, padding: 10 }}>
                    {(userData.tripList || []).map((x, index) => {
                      return (
                        <StepItemWidget
                          itemInfo={x}
                          onCheck={onCheck}
                          key={index}
                          camperIndex={key}
                          isLast={index == (userData.tripList || []).length - 1}
                          userData={userData}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>}
        {regType == "true" && (
          <>
            {(userData.waiverList || []).map((waiver_info, index) => {
              return <div style={{ marginTop: 20 }} key={waiver_info.ID}>
                <div className="block">
                  <div
                    className="title"
                    style={{ textAlign: "center", paddingTop: 5 }}
                  >
                    {waiver_info.waiver_title}
                  </div>
                  <div style={{ marginTop: 10, fontWeight: 600 }}>
                    {waiver_info.waiver_text.replace(/\\n/g, "\n").split("\n").map((section, secKey) => {
                      return <div key={secKey}>{renderTextWithLinks(section)}</div>;
                    })}
                  </div>
                  {waiver_info.allow_no == "1" &&
                    <Checkbox
                      onChange={(e) => {
                        let tmpList = [...userData.waiverList]
                        tmpList[index].allow_no_checked = e.target.checked;
                        setUserData({
                          ...userData,
                          waiverList: tmpList,
                        });
                      }}
                      checked={waiver_info.allow_no_checked}
                    >
                      <div style={{ marginTop: 20, fontSize: 15 }}>
                        I DO NOT AGREE
                      </div>
                    </Checkbox>
                  }
                  <div
                    style={{
                      fontWeight: 600,
                      marginTop: 20,
                      marginBottom: 20,
                      color: "var(--blueColor)",
                    }}
                  >
                    I have read and understand the above <br></br>Electronic Signature
                  </div>
                  <Row gutter={[16, 16]} align={"bottom"}>
                    <Col xs={24} sm={10}>
                      <div style={{ paddingBottom: "5px" }}>
                        Enter First Name*
                      </div>
                      <Input
                        value={waiver_info.first_name}
                        disabled={waiver_info.allow_no_checked}
                        className={
                          errorField !== undefined &&
                            errorField.includes(index + "first_name")
                            ? `common-input-error`
                            : `common-input`
                        }
                        onChange={(e) => {
                          if (errorField.includes(index + "first_name")) {
                            let errors = errorField.filter(
                              (y) => y != index + "first_name"
                            );
                            setErrorField([...errors]);
                          }
                          let tmpList = [...userData.waiverList]
                          tmpList[index].first_name = capitalizeFirstLetter(
                            e.target.value
                          );
                          setUserData({
                            ...userData,
                            waiverList: tmpList,
                          });
                        }}
                      />
                    </Col>
                    <Col xs={24} sm={10}>
                      <div style={{ paddingBottom: "5px" }}>Enter Last Name*</div>
                      <Input
                        value={waiver_info.last_name}
                        disabled={waiver_info.allow_no_checked}
                        className={
                          errorField !== undefined &&
                            errorField.includes(index + "last_name")
                            ? `common-input-error`
                            : `common-input`
                        }
                        onChange={(e) => {
                          if (errorField.includes(index + "last_name")) {
                            let errors = errorField.filter(
                              (y) => y != index + "last_name"
                            );
                            setErrorField([...errors]);
                          }
                          let tmpList = [...userData.waiverList]
                          tmpList[index].last_name = capitalizeFirstLetter(
                            e.target.value
                          );
                          setUserData({
                            ...userData,
                            waiverList: tmpList,
                          });
                        }}
                      />
                    </Col>
                    <Col xs={24} sm={4} style={{ paddingBottom: 7 }}>
                      {!waiver_info.allow_no_checked &&
                        <CommonButton onClick={() => {
                          let firstName = "";
                          let lastName = "";
                          
                          // Check if this is a facility registration type
                          const regTypeValue = (appInfo?.registration_info?.registration_type || "")
                            .toString()
                            .trim()
                            .toLowerCase();
                          const isFacilityType = regTypeValue === "facility";
                          console.log('[Waiver Sign] facility?', regTypeValue, {
                            contact_first_name: userData.contact_first_name,
                            contact_last_name: userData.contact_last_name,
                            p_first_name: userData.p_first_name,
                            p_last_name: userData.p_last_name,
                            camper_first_name: userData.camperInfo?.[0]?.camper_first_name,
                            camper_last_name: userData.camperInfo?.[0]?.camper_last_name
                          });
                          
                          if (isFacilityType) {
                            // For facility registrations, use contact_first_name and contact_last_name
                            firstName = userData.contact_first_name || userData.camperInfo?.[0]?.contact_first_name || "";
                            lastName = userData.contact_last_name || userData.camperInfo?.[0]?.contact_last_name || "";
                          } else {
                            // For camp registrations, use fallback logic:
                            // 1. Try parent first/last name
                            // 2. If not available, use first camper's first/last name
                            // 3. If not available, use emergency contact first/last name
                            firstName = userData.p_first_name;
                            lastName = userData.p_last_name;
                            
                            if (!firstName || !lastName) {
                              // Try camper info from first participant
                              if (userData.camperInfo && userData.camperInfo.length > 0) {
                                firstName = firstName || userData.camperInfo[0].camper_first_name;
                                lastName = lastName || userData.camperInfo[0].camper_last_name;
                              }
                            }
                            
                            if (!firstName || !lastName) {
                              // Try emergency contact
                              firstName = firstName || userData.c_first_name;
                              lastName = lastName || userData.c_last_name;
                            }
                          }

                          console.log('[Waiver Sign] selected names', { firstName, lastName });
                          let tmpList = [...userData.waiverList]
                          tmpList[index].first_name = firstName || "";
                          tmpList[index].last_name = lastName || "";
                          setUserData({
                            ...userData,
                            waiverList: tmpList,
                          });

                        }}>
                          Sign
                        </CommonButton>}
                    </Col>
                  </Row>
                  <Checkbox
                    disabled={waiver_info.allow_no_checked}
                    onChange={(e) => {
                      let tmpList = [...userData.waiverList]
                      tmpList[index].checked = e.target.checked;
                      setUserData({
                        ...userData,
                        waiverList: tmpList,
                      });
                    }}
                    checked={waiver_info.checked}
                  >
                    <div style={{ marginTop: 20, fontSize: 15 }}>
                      I agree and understand that
                      all electronic signatures are the legal equivalent of my
                      manual/handwritten signature and I consent to be legally
                      bound to this agreement. I further agree my signature on
                      this page is as valid as if I signed the document in
                      writing.
                    </div>
                  </Checkbox>
                </div>
              </div>
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default StepFee;
